import { devLog, devError } from '../utils/devLogger';
// Programs Management Service with Firebase Integration
import { db } from '../firebase';
import { withRetry } from '../utils/firebaseRetry';
import { 
  collection, 
  doc, 
  addDoc, 
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  onSnapshot,
  writeBatch
} from 'firebase/firestore';

class ProgramsService {
  constructor() {
    this.listeners = new Map();
  }

  // Create a new program
  async createProgram(programData) {

    try {
      const program = {
        name: programData.name,
        description: programData.description,
        modules: programData.modules || [],
        duration: programData.duration || 0,
        difficulty: programData.difficulty || 'beginner',
        prerequisites: programData.prerequisites || [],
        learningObjectives: programData.learningObjectives || [],
        isActive: programData.isActive !== false,
        accreditation: {
          provider: programData.accreditation?.provider || null,
          certificateType: programData.accreditation?.certificateType || null,
          validityPeriod: programData.accreditation?.validityPeriod || null
        },
        pricing: {
          amount: programData.pricing?.amount || 0,
          currency: programData.pricing?.currency || 'USD',
          paymentOptions: programData.pricing?.paymentOptions || []
        },
        metadata: {
          createdBy: programData.createdBy,
          tags: programData.tags || [],
          category: programData.category || 'general',
          targetAudience: programData.targetAudience || []
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await withRetry(() => addDoc(collection(db, 'programs'), program));
      return {
        id: docRef.id,
        ...program,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      devError('Error creating program:', error);
      throw error;
    }
  }


  // Get all programs
  async getPrograms(activeOnly = false) {

    try {
      let q = query(collection(db, 'programs'), orderBy('createdAt', 'desc'));
      
      if (activeOnly) {
        q = query(collection(db, 'programs'), where('isActive', '==', true), orderBy('createdAt', 'desc'));
      }

      const querySnapshot = await getDocs(q);
      const programs = [];

      querySnapshot.forEach((doc) => {
        programs.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()?.toISOString() || null,
          updatedAt: doc.data().updatedAt?.toDate()?.toISOString() || null
        });
      });

      return programs;
    } catch (error) {
      devError('Error getting programs:', error);
      return [];
    }
  }


  // Get a specific program by ID
  async getProgram(programId) {

    try {
      const programRef = doc(db, 'programs', programId);
      const docSnapshot = await getDoc(programRef);

      if (!docSnapshot.exists()) {
        return null;
      }

      return {
        id: docSnapshot.id,
        ...docSnapshot.data(),
        createdAt: docSnapshot.data().createdAt?.toDate()?.toISOString() || null,
        updatedAt: docSnapshot.data().updatedAt?.toDate()?.toISOString() || null
      };
    } catch (error) {
      devError('Error getting program:', error);
      return null;
    }
  }


  // Update a program
  async updateProgram(programId, updateData) {

    try {
      const programRef = doc(db, 'programs', programId);
      
      const updates = {
        ...updateData,
        updatedAt: serverTimestamp()
      };

      await withRetry(() => updateDoc(programRef, updates));
      return await this.getProgram(programId);
    } catch (error) {
      devError('Error updating program:', error);
      throw error;
    }
  }


  // Delete a program (soft delete by setting isActive to false)
  async deleteProgram(programId) {

    try {
      const programRef = doc(db, 'programs', programId);
      
      await updateDoc(programRef, {
        isActive: false,
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      devLog('Program soft deleted successfully:', programId);
      return true;
    } catch (error) {
      devError('Error deleting program:', error);
      throw error;
    }
  }


  // Hard delete a program (permanent deletion)
  async hardDeleteProgram(programId) {

    try {
      const programRef = doc(db, 'programs', programId);
      await deleteDoc(programRef);
      
      devLog('Program permanently deleted:', programId);
      return true;
    } catch (error) {
      devError('Error permanently deleting program:', error);
      throw error;
    }
  }

  // Add module to program
  async addModuleToProgram(programId, moduleId) {

    try {
      const programRef = doc(db, 'programs', programId);
      const programDoc = await getDoc(programRef);

      if (!programDoc.exists()) {
        throw new Error('Program not found');
      }

      const currentModules = programDoc.data().modules || [];
      if (!currentModules.includes(moduleId)) {
        await updateDoc(programRef, {
          modules: [...currentModules, moduleId],
          updatedAt: serverTimestamp()
        });
      }

      devLog(`Module ${moduleId} added to program ${programId}`);
      return true;
    } catch (error) {
      devError('Error adding module to program:', error);
      throw error;
    }
  }


  // Remove module from program
  async removeModuleFromProgram(programId, moduleId) {

    try {
      const programRef = doc(db, 'programs', programId);
      const programDoc = await getDoc(programRef);

      if (!programDoc.exists()) {
        throw new Error('Program not found');
      }

      const currentModules = programDoc.data().modules || [];
      const updatedModules = currentModules.filter(id => id !== moduleId);

      await updateDoc(programRef, {
        modules: updatedModules,
        updatedAt: serverTimestamp()
      });

      devLog(`Module ${moduleId} removed from program ${programId}`);
      return true;
    } catch (error) {
      devError('Error removing module from program:', error);
      throw error;
    }
  }


  // Setup real-time listener for programs
  setupProgramsListener(callback, activeOnly = false) {

    let q = query(collection(db, 'programs'), orderBy('createdAt', 'desc'));
    
    if (activeOnly) {
      q = query(collection(db, 'programs'), where('isActive', '==', true), orderBy('createdAt', 'desc'));
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const programs = [];
      
      snapshot.docs.forEach(doc => {
        programs.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()?.toISOString() || null,
          updatedAt: doc.data().updatedAt?.toDate()?.toISOString() || null
        });
      });

      callback(programs);
    }, (error) => {
      devError('Error listening to programs:', error);
    });

    this.listeners.set('programs', unsubscribe);
    return unsubscribe;
  }


  // Get programs by category
  async getProgramsByCategory(category) {

    try {
      const q = query(
        collection(db, 'programs'),
        where('metadata.category', '==', category),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const programs = [];

      querySnapshot.forEach((doc) => {
        programs.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()?.toISOString() || null,
          updatedAt: doc.data().updatedAt?.toDate()?.toISOString() || null
        });
      });

      return programs;
    } catch (error) {
      devError('Error getting programs by category:', error);
      return [];
    }
  }


  // Get programs with module details
  async getProgramsWithModules() {

    try {
      const programs = await this.getPrograms(true);
      
      // Fetch module details for each program
      const programsWithModules = await Promise.all(
        programs.map(async (program) => {
          const moduleDetails = await Promise.all(
            program.modules.map(async (moduleId) => {
              try {
                const moduleRef = doc(db, 'modules', moduleId);
                const moduleDoc = await getDoc(moduleRef);
                return moduleDoc.exists() ? { id: moduleId, ...moduleDoc.data() } : null;
              } catch (error) {
                devLog(`Could not fetch module ${moduleId}:`, error);
                return null;
              }
            })
          );

          return {
            ...program,
            moduleDetails: moduleDetails.filter(Boolean)
          };
        })
      );

      return programsWithModules;
    } catch (error) {
      devError('Error getting programs with modules:', error);
      return [];
    }
  }


  // Enroll user in program
  async enrollUserInProgram(userId, programId) {

    try {
      const batch = writeBatch(db);

      // Update user document with program enrollment
      const userRef = doc(db, 'users', userId);
      batch.update(userRef, {
        currentProgram: programId,
        enrolledAt: serverTimestamp(),
        programStatus: 'enrolled',
        updatedAt: serverTimestamp()
      });

      // Create enrollment record
      const enrollmentRef = doc(collection(db, 'users', userId, 'enrollments'));
      batch.set(enrollmentRef, {
        programId,
        enrolledAt: serverTimestamp(),
        status: 'active',
        progress: 0
      });

      await batch.commit();
      
      devLog(`User ${userId} enrolled in program ${programId}`);
      return true;
    } catch (error) {
      devError('Error enrolling user in program:', error);
      throw error;
    }
  }


  // Get user's enrolled programs
  async getUserPrograms(userId) {

    try {
      const enrollmentsRef = collection(db, 'users', userId, 'enrollments');
      const q = query(enrollmentsRef, where('status', '==', 'active'), orderBy('enrolledAt', 'desc'));
      
      const querySnapshot = await getDocs(q);
      const programs = [];

      for (const doc of querySnapshot.docs) {
        const enrollmentData = doc.data();
        const program = await this.getProgram(enrollmentData.programId);
        
        if (program) {
          programs.push({
            ...program,
            enrollment: {
              ...enrollmentData,
              enrolledAt: enrollmentData.enrolledAt?.toDate()?.toISOString() || null
            }
          });
        }
      }

      return programs;
    } catch (error) {
      devError('Error getting user programs:', error);
      return [];
    }
  }


  // Get program completion statistics
  async getProgramStatistics(programId) {

    try {
      // Get all users enrolled in this program
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('currentProgram', '==', programId));
      
      const querySnapshot = await getDocs(q);
      
      let totalEnrolled = 0;
      let completed = 0;
      let inProgress = 0;
      
      for (const userDoc of querySnapshot.docs) {
        totalEnrolled++;
        const userId = userDoc.id;
        
        // Check if user has completed the program
        const completedModulesRef = collection(db, 'users', userId, 'completedModules');
        const completedSnapshot = await getDocs(completedModulesRef);
        
        const program = await this.getProgram(programId);
        const requiredModules = program?.modules || [];
        
        const completedModuleIds = completedSnapshot.docs.map(doc => doc.id);
        const hasCompletedAllModules = requiredModules.every(moduleId => 
          completedModuleIds.includes(moduleId)
        );
        
        if (hasCompletedAllModules) {
          completed++;
        } else {
          inProgress++;
        }
      }

      return {
        totalEnrolled,
        completed,
        inProgress,
        completionRate: totalEnrolled > 0 ? (completed / totalEnrolled) * 100 : 0,
        dropoutRate: totalEnrolled > 0 ? ((totalEnrolled - completed - inProgress) / totalEnrolled) * 100 : 0
      };
    } catch (error) {
      devError('Error getting program statistics:', error);
      return null;
    }
  }


  // Clean up listeners
  cleanup() {
    this.listeners.forEach((unsubscribe) => {
      unsubscribe();
    });
    this.listeners.clear();
    devLog('🔧 Programs service: All listeners cleaned up');
  }
}

const programsService = new ProgramsService();
export default programsService;